import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { AppState } from '../../../core.state';
import { NotificationService } from '../../../notifications/notification.service';
import { CountryCard } from '../country-card.model';
import { CountryCardService } from '../country-card.service';
import {
  CountryCardActionTypes,
  CreateCountryCard,
  CreateCountryCardFail,
  CreateCountryCardSuccess,
  DeleteCountryCard,
  DeleteCountryCardFail,
  DeleteCountryCardSuccess,
  LoadCountryCards,
  LoadCountryCardsFail,
  LoadCountryCardsSuccess,
  UpdateCountryCard,
  UpdateCountryCardFail,
  UpdateCountryCardSuccess,
} from './country-card.actions';
import { selectCountryCardsCompanyId } from './country-card.selectors';

@Injectable()
export class CountryCardEffects {
  constructor(
    private service: CountryCardService,
    private actions$: Actions,
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private location: Location
  ) {}

  @Effect({ dispatch: false })
  genericFail$ = this.actions$.pipe(
    ofType<Action & { payload: string }>(
      CountryCardActionTypes.LoadCountryCardsFail,
      CountryCardActionTypes.UpdateCountryCardFail,
      CountryCardActionTypes.CreateCountryCardFail,
      CountryCardActionTypes.DeleteCountryCardFail
    ),
    tap((action) => this.notificationService.error(action.payload))
  );

  @Effect()
  loadCountryCards$ = this.actions$.pipe(
    ofType<LoadCountryCards>(CountryCardActionTypes.LoadCountryCards),
    concatMap((cc) =>
      of(cc).pipe(
        withLatestFrom(this.store.select(selectCountryCardsCompanyId))
      )
    ),
    mergeMap(([action, companyId]) =>
      this.service.getByCompanyId(companyId).pipe(
        map((items) => {
          items.forEach((item) => this.localizeCountryCard(item));
          return new LoadCountryCardsSuccess(items);
        }),
        catchError((err) => of(new LoadCountryCardsFail(err)))
      )
    )
  );

  @Effect()
  updateCountryCard$ = this.actions$.pipe(
    ofType<UpdateCountryCard>(CountryCardActionTypes.UpdateCountryCard),
    mergeMap((action) =>
      this.service
        .put(action.countryCard.id, {
          ...action.countryCard,
          createdUser: undefined,
        })
        .pipe(
          map((result) => {
            this.localizeCountryCard(result);
            return new UpdateCountryCardSuccess(result);
          }),
          catchError((err) => {
            let error = '';
            if (err.errors) {
              Object.keys(err.errors).forEach(
                (key) => (error += `${key}: ${err.errors[key]}. `)
              );
            } else {
              error = err;
            }
            return of(new UpdateCountryCardFail(error));
          })
        )
    )
  );

  @Effect({ dispatch: false })
  updateCountryCardSuccess$ = this.actions$.pipe(
    ofType(CountryCardActionTypes.UpdateCountryCardSuccess),
    tap((action: UpdateCountryCardSuccess) => {
      this.notificationService.success(
        this.translateService.instant('Country card modificata con successo')
      );
      this.location.back();
    })
  );

  @Effect()
  createCountryCard$ = this.actions$.pipe(
    ofType<CreateCountryCard>(CountryCardActionTypes.CreateCountryCard),
    mergeMap((action) =>
      this.service.post(action.countryCard).pipe(
        map((result) => {
          this.localizeCountryCard(result);
          return new CreateCountryCardSuccess(result);
        }),
        catchError((err) => of(new CreateCountryCardFail(err)))
      )
    )
  );

  @Effect({ dispatch: false })
  createCountryCardSuccess$ = this.actions$.pipe(
    ofType(CountryCardActionTypes.CreateCountryCardSuccess),
    tap((action: CreateCountryCardSuccess) => {
      this.notificationService.success(
        this.translateService.instant('Country card creata con successo')
      );
      this.location.back();
    })
  );

  @Effect()
  deleteCountryCard$ = this.actions$.pipe(
    ofType<DeleteCountryCard>(CountryCardActionTypes.DeleteCountryCard),
    mergeMap((action) =>
      this.service.delete(action.countryCard.id).pipe(
        map((result) => new DeleteCountryCardSuccess(action.countryCard)),
        catchError((err) => of(new DeleteCountryCardFail(err)))
      )
    )
  );

  @Effect({ dispatch: false })
  deleteCountryCardSuccess$ = this.actions$.pipe(
    ofType(CountryCardActionTypes.DeleteCountryCardSuccess),
    tap((action: DeleteCountryCardSuccess) => {
      this.notificationService.success(
        this.translateService.instant('CountryCard eliminata con successo')
      );
      this.location.back();
    })
  );

  private localizeCountryCard(item: CountryCard) {
    item.country.name = this.translateService.instant(item.country.name);
    item.certificationTypes.forEach(
      (certification) =>
        (certification.name = this.translateService.instant(certification.name))
    );
    item.compulsoryPublicHealthRecordTypes.forEach(
      (phr) => (phr.name = this.translateService.instant(phr.name))
    );
    item.notCompulsoryPublicHealthRecordTypes.forEach(
      (phr) => (phr.name = this.translateService.instant(phr.name))
    );
  }
}
